// src/components/ChatWidget.js
// Widget flottant d'aide HmongHearts — aliment par Cloud Function Anthropic Haiku

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircleQuestion, X, Send } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const SUGGESTION_KEYS = [
  'chatWidget.suggestion1',
  'chatWidget.suggestion2',
  'chatWidget.suggestion3',
  'chatWidget.suggestion4',
];

const stripMarkdown = (text) => {
  return text
    .replace(/#{1,6}\s?/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^\d+\.\s/gm, '• ')
    .replace(/^[-*]\s/gm, '• ')
    .trim();
};

const ChatWidget = ({ user }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll en bas quand nouveaux messages
  useEffect(() => {
    if (messagesEndRef.current?.scrollIntoView) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const handleSend = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const chatWithAssistant = httpsCallable(functions, 'chatWithAssistant');
      const result = await chatWithAssistant({
        message: trimmed,
        history: messages,
        lang: i18n.language.startsWith('en') ? 'en' : 'fr',
      });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: stripMarkdown(result.data.reply) },
      ]);
    } catch (err) {
      setError(err.message || t('chatWidget.defaultError'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Bulle flottante */}
      {!isOpen && (
        <button
          data-testid="chatwidget-bubble"
          onClick={() => setIsOpen(true)}
          aria-label={t('chatWidget.ariaOpen')}
          className={`fixed ${user ? 'bottom-20' : 'bottom-6'} right-4 md:bottom-6 md:right-6 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center ${messages.length === 0 ? 'animate-pulse' : ''}`}
        >
          <MessageCircleQuestion size={22} />
        </button>
      )}

      {/* Panneau chat */}
      {isOpen && (
        <div
          data-testid="chatwidget-panel"
          role="dialog"
          aria-label={t('chatWidget.ariaPanel')}
          className={`fixed ${user ? 'bottom-16' : 'bottom-2'} right-2 left-2 md:bottom-6 md:right-6 md:left-auto md:w-96 z-50 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col overflow-hidden transition-all duration-300`}
          style={{ height: 'min(85vh, 500px)', maxHeight: 'calc(100vh - 5rem)' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-500 to-fuchsia-600 text-white p-4 flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-lg">
              🤖
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">{t('chatWidget.headerTitle')}</p>
              <p className="text-xs text-white/70">{t('chatWidget.headerSubtitle')}</p>
            </div>
            <button
              data-testid="chatwidget-close"
              onClick={() => setIsOpen(false)}
              aria-label={t('chatWidget.ariaClose')}
              className="hover:bg-white/20 rounded-full p-1.5 transition"
            >
              <X size={20} />
            </button>
          </div>

          {/* Zone messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" aria-live="polite">
            {/* Message d'accueil */}
            <div className="flex justify-start">
              <div
                data-testid="chatwidget-message-assistant"
                className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-bl-md text-sm bg-gray-100 dark:bg-gray-800 dark:text-gray-200"
              >
                {t('chatWidget.welcome')}
              </div>
            </div>

            {/* Suggestions */}
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {SUGGESTION_KEYS.map((key, i) => (
                  <button
                    key={i}
                    data-testid="chatwidget-suggestion"
                    onClick={() => handleSend(t(key))}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition"
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  data-testid={`chatwidget-message-${msg.role}`}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-rose-500 text-white rounded-br-md'
                      : 'bg-gray-100 dark:bg-gray-800 dark:text-gray-200 rounded-bl-md'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Loading */}
            {loading && (
              <div className="flex justify-start">
                <div
                  data-testid="chatwidget-loading"
                  className="px-4 py-3 rounded-2xl rounded-bl-md bg-gray-100 dark:bg-gray-800 flex gap-1.5"
                >
                  <span
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
              </div>
            )}

            {/* Erreur */}
            {error && (
              <div
                data-testid="chatwidget-error"
                className="text-center text-xs text-red-500 dark:text-red-400 py-1"
              >
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Zone input */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2 flex-shrink-0">
            <input
              data-testid="chatwidget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chatWidget.inputPlaceholder')}
              maxLength={500}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 dark:text-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/50 disabled:opacity-50"
            />
            <button
              data-testid="chatwidget-send"
              onClick={() => handleSend()}
              disabled={loading || !input.trim()}
              aria-label={t('chatWidget.ariaSend')}
              className="w-11 h-11 bg-gradient-to-r from-rose-500 to-fuchsia-600 text-white rounded-full flex items-center justify-center hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
